import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AUTH_SESSION } from './auth-session';
import { Conversation } from './conversation';

@Component({
    selector: 'app-chat-page',
    template: `
        <header class="bar">
            <h1>Bannerflow Demo Agent</h1>
            <div class="actions">
                <button
                    type="button"
                    class="new-conversation"
                    (click)="conversation.reset()">
                    New conversation
                </button>
                @if (session.userName(); as userName) {
                    <span class="user">{{ userName }}</span>
                }
                <button
                    type="button"
                    class="sign-out"
                    (click)="session.logout()">
                    Sign out
                </button>
            </div>
        </header>

        <main class="messages">
            @for (message of conversation.messages(); track $index) {
                <div class="message {{ message.role }}">{{ message.text }}</div>
            } @empty {
                <p class="hint">Ask the agent something. It answers as you, through agent-bff.</p>
            }
            @if (conversation.busy()) {
                <div
                    class="thinking"
                    aria-live="polite">
                    Thinking…
                </div>
            }
            @if (conversation.error(); as error) {
                <div
                    class="error"
                    role="alert">
                    {{ error }}
                </div>
            }
        </main>

        <form
            #form
            class="composer"
            (submit)="submit($event, form)">
            <textarea
                name="message"
                rows="2"
                placeholder="Message the agent"
                [value]="draft()"
                (input)="updateDraft($event)"
                (keydown)="onKeydown($event, form)"></textarea>
            <button
                type="submit"
                [disabled]="conversation.busy() || draft().trim() === ''">
                Send
            </button>
        </form>
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            height: 100vh;
            max-width: 56rem;
            margin: 0 auto;
            font-family: system-ui, sans-serif;
        }

        .bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem 1rem;
            border-bottom: 1px solid #ddd;

            h1 {
                margin: 0;
                font-size: 1.1rem;
            }

            .actions {
                display: flex;
                align-items: center;
                gap: 0.75rem;
            }
        }

        .messages {
            flex: 1;
            overflow-y: auto;
            padding: 1rem;
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .message {
            max-width: 80%;
            padding: 0.6rem 0.9rem;
            border-radius: 0.75rem;
            white-space: pre-wrap;
            line-height: 1.4;

            &.user {
                align-self: flex-end;
                background: #1b64f2;
                color: #fff;
            }

            &.assistant {
                align-self: flex-start;
                background: #f1f2f4;
            }
        }

        .hint,
        .thinking {
            color: #666;
        }

        .error {
            padding: 0.6rem 0.9rem;
            border-radius: 0.5rem;
            background: #fdecea;
            color: #8a1c12;
        }

        .composer {
            display: flex;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            border-top: 1px solid #ddd;

            textarea {
                flex: 1;
                resize: none;
                padding: 0.5rem;
                font: inherit;
            }
        }

        button {
            padding: 0.5rem 0.9rem;
            border: 1px solid #bbb;
            border-radius: 0.4rem;
            background: #fff;
            cursor: pointer;

            &[type='submit'] {
                background: #1b64f2;
                border-color: #1b64f2;
                color: #fff;
            }

            &:disabled {
                opacity: 0.5;
                cursor: default;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatPageComponent {
    readonly conversation = inject(Conversation);
    readonly session = inject(AUTH_SESSION);
    readonly draft = signal('');

    submit(event: Event, form: HTMLFormElement): void {
        event.preventDefault();
        const text = this.draft();
        this.draft.set('');
        form.reset();
        void this.conversation.send(text);
    }

    /** Enter sends; Shift+Enter inserts a line break. */
    onKeydown(event: KeyboardEvent, form: HTMLFormElement): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            this.submit(event, form);
        }
    }

    updateDraft(event: Event): void {
        this.draft.set((event.target as HTMLTextAreaElement).value);
    }
}
